import { RadioGroup, Select, TextInput } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import classnames from 'classnames';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { EventCreationDayPicker } from 'src/Components/Forms/Event/EventForm/EventCreationDayPicker';
import { EventCreationMonthPicker } from 'src/Components/Forms/Event/EventForm/EventCreationMonthPicker';
import { EventCreationWeekDayPicker } from 'src/Components/Forms/Event/EventForm/EventCreationWeekDayPicker';
import { TransformValues } from 'src/types/transform-values.type';
import getNestedValue, { NestedObject } from 'src/Utils/getNestedValue';

import { EventCreationAdvancedMonthDaySelect } from '../EventCreationAdvancedMonthDaySelect';

import styles from './EventRecurrenceCreationForm.module.scss';
import { Props } from './EventRecurrenceCreationForm.types';

const getFrequencyOptions = (t: TFunction<'translation', undefined>, interval: number) => [
  {
    value: 'DAILY',
    label: t('createEventForm.customDaily', {
      count: interval,
      defaultValue_one: 'jour',
      defaultValue_other: 'jours',
    }),
  },
  {
    value: 'WEEKLY',
    label: t('createEventForm.customWeekly', {
      count: interval,
      defaultValue_one: 'semaine',
      defaultValue_other: 'semaines',
    }),
  },
  {
    value: 'MONTHLY',
    label: t('createEventForm.customMonthly', {
      count: interval,
      defaultValue_one: 'mois',
      defaultValue_other: 'mois',
    }),
  },
  {
    value: 'YEARLY',
    label: t('createEventForm.customYearly', {
      count: interval,
      defaultValue_one: 'an',
      defaultValue_other: 'ans',
    }),
  },
];

const EventRecurrenceCreationForm = <T extends NestedObject, O extends TransformValues<T>>({
  form,
  paths = {
    frequency: 'recurrence.frequency',
    interval: 'recurrence.interval',
    customFrequency: 'recurrence.customFrequency',
    monthDayNumberOrAdvanced: 'recurrence.monthDayNumberOrAdvanced',
    weekDays: 'recurrence.weekDays',
    monthDayNumber: 'recurrence.monthDayNumber',
    month: 'recurrence.byMonth',
    advancedMonthDay: 'recurrence.byWeekDay',
    advancedMonthWeek: 'recurrence.byMonthDay',
    monthDaySelected: 'recurrence.monthDaySelected',
    until: 'recurrence.until',
  },
  translationOverrides = {},
}: Props<T, O>) => {
  const { t } = useTranslation();

  const frequencyValue = getNestedValue(form.values, paths.frequency);
  const intervalValue = getNestedValue(form.values, paths.interval) as string | undefined;
  const customFrequencyValue = getNestedValue(form.values, paths.customFrequency);

  const interval = parseInt(intervalValue ?? '1') ?? 1;
  const frequencyOptions = getFrequencyOptions(t, interval);
  const intervalError = form.errors[paths.interval];

  return (
    <div className={styles.container}>
      <Select
        className={classnames({
          [styles.customSelect]: frequencyValue === 'CUSTOM',
        })}
        placeholder={translationOverrides.none ?? t('createEventForm.none', 'Aucune')}
        label={translationOverrides.recurrence ?? t('createEventForm.recurrence', 'Récurrence')}
        data={[
          {
            value: 'DAILY',
            label: translationOverrides.daily ?? t('createEventForm.daily', 'Tous les jours'),
          },
          {
            value: 'WEEKLY',
            label:
              translationOverrides.weekly ?? t('createEventForm.weekly', 'Toutes les semaines'),
          },
          {
            value: 'MONTHLY',
            label: translationOverrides.monthly ?? t('createEventForm.monthly', 'Tous les mois'),
          },
          {
            value: 'YEARLY',
            label: translationOverrides.yearly ?? t('createEventForm.yearly', 'Tous les ans'),
          },
          {
            value: 'CUSTOM',
            label: translationOverrides.custom ?? t('createEventForm.custom', 'Personnaliser'),
          },
        ]}
        {...form.getInputProps(paths.frequency)}
      />

      {frequencyValue === 'CUSTOM' && (
        <div className={styles.customContainer}>
          <div className={styles.customFrequencyRow}>
            <div className={styles.label}>
              {translationOverrides.every ?? t('createEventForm.every', 'Tous les')}
            </div>
            <TextInput
              className={styles.intervalInput}
              type={'number'}
              min={1}
              {...form.getInputProps(paths.interval)}
              error={!!form.errors[paths.interval]}
              errorProps={{
                type: 'default',
                children: null,
              }}
            />
            <Select
              allowDeselect={false}
              data={frequencyOptions}
              {...form.getInputProps(paths.customFrequency)}
            />
          </div>
          {intervalError && <div className={styles.error}>{intervalError}</div>}
          {customFrequencyValue === 'YEARLY' && (
            <div className={styles.customColumn}>
              <EventCreationMonthPicker form={form} pathMonthValue={paths.month} />
              <EventCreationAdvancedMonthDaySelect
                form={form}
                pathMonthDayNumberOrAdvanced={paths.monthDayNumberOrAdvanced}
                pathAdvancedMonthDay={paths.advancedMonthDay}
                pathAdvancedMonthWeek={paths.advancedMonthWeek}
              />
            </div>
          )}
          {customFrequencyValue === 'MONTHLY' && (
            <div className={styles.customColumn}>
              <RadioGroup {...form.getInputProps(paths.monthDayNumberOrAdvanced)}>
                <EventCreationDayPicker
                  form={form}
                  pathMonthDayNumber={paths.monthDayNumber}
                  pathMonthDayNumberOrAdvanced={paths.monthDayNumberOrAdvanced}
                />
                <EventCreationAdvancedMonthDaySelect
                  form={form}
                  pathMonthDayNumberOrAdvanced={paths.monthDayNumberOrAdvanced}
                  pathAdvancedMonthDay={paths.advancedMonthDay}
                  pathAdvancedMonthWeek={paths.advancedMonthWeek}
                  pathMonthDaySelected={paths.monthDaySelected}
                />
              </RadioGroup>
            </div>
          )}
          {customFrequencyValue === 'WEEKLY' && (
            <EventCreationWeekDayPicker form={form} pathWeekDays={paths.weekDays} />
          )}
        </div>
      )}
      <DateTimePicker
        className={styles.recurrenceEndDatePicker}
        style={{ width: '100%' }}
        placeholder={t('createEventForm.noRecurrenceEnd', 'Récurrence sans fin')}
        label={t('createEventForm.recurrenceUntilShort', 'Fin de la récurrence')}
        disabled={!frequencyValue}
        clearable
        {...form.getInputProps(paths.until)}
      />
    </div>
  );
};

export default EventRecurrenceCreationForm;
